/**
 * S-BRANCH lookup rules (TL ruling Q1, option a): a branch's counts and
 * freshness come from its row in the TPIN's `/branches` list. The row is either
 * already loaded (the overview put it there), or the list is walked page by page
 * until the row is found. Presentation only - nothing here computes a count.
 */

import type { BranchesView, FreshnessState, TpinBranch } from './tpin'

export type BranchLookup =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  /** The row exists; `refreshing` marks it as previously retrieved data. */
  | { readonly kind: 'found'; readonly row: TpinBranch; readonly refreshing: boolean }
  /** Not loaded yet, more pages exist and none is in flight: the next page is due. */
  | { readonly kind: 'advance' }
  /** Not loaded yet and a page (or a refresh) is in flight. */
  | { readonly kind: 'searching' }
  /** A page failed while walking; the user retries explicitly. */
  | { readonly kind: 'stalled'; readonly message: string }
  /** Every page was read (or the cursor ran past the end) and the row is not there. */
  | { readonly kind: 'not-found' }

export function branchLookup(view: BranchesView, dbName: string): BranchLookup {
  if (view.kind === 'loading') return { kind: 'loading' }
  if (view.kind === 'error') return { kind: 'error', message: view.message }
  const row = view.list.rows.find((candidate) => candidate.dbName === dbName)
  if (row !== undefined) return { kind: 'found', row, refreshing: view.refreshing }
  if (!view.list.hasMore) return { kind: 'not-found' }
  if (view.more.kind === 'error') return { kind: 'stalled', message: view.more.message }
  return view.more.kind === 'loading' || view.refreshing ? { kind: 'searching' } : { kind: 'advance' }
}

/** True while an explicit refresh is re-reading a row that is already on screen. */
export function lookupRefreshing(lookup: BranchLookup): boolean {
  return lookup.kind === 'found' && lookup.refreshing
}

/**
 * What the context bar shows for the branch's source timestamp: pending while
 * the row is being found, the row's own stamp when it answered, and an honest
 * absence for everything else (unreachable, unknown, failed lookups).
 */
export function branchFreshness(lookup: BranchLookup): FreshnessState {
  switch (lookup.kind) {
    case 'loading':
    case 'advance':
    case 'searching':
      return { kind: 'pending' }
    case 'found':
      return lookup.row.kind === 'reachable' && lookup.row.freshness !== null
        ? { kind: 'stamp', raw: lookup.row.freshness }
        : { kind: 'absent' }
    default:
      return { kind: 'absent' }
  }
}

/** A reachable branch with nothing in the period (compares server counts; adds nothing). */
export function hasNoInvoices(row: { readonly synced: number; readonly failed: number; readonly pending: number }): boolean {
  return row.synced === 0 && row.failed === 0 && row.pending === 0
}
